import { renderSVG } from 'uqr';

export interface RoomInvitePayload {
  title: string;
  text: string;
  url: string;
}

export interface RoomInviteOptions {
  checkWord?: string;
  appUrl?: string;
}

export interface PrefilledInvite {
  roomCode: string;
  checkWord: string;
}

export type ShareResult = 'shared' | 'copied' | 'cancelled' | 'unsupported';

export function buildJoinUrl(code: string, options: RoomInviteOptions = {}): string {
  const roomCode = code.trim().toUpperCase();
  const checkWord = options.checkWord?.trim() || '';
  return buildInviteUrl(roomCode, checkWord, options.appUrl);
}

/** Phone page that can join or forward the invite. Not the join URL itself. */
export function buildHandoffUrl(code: string, options: RoomInviteOptions = {}): string {
  const url = new URL(buildJoinUrl(code, options));
  url.searchParams.set('screen', 'share');
  return url.toString();
}

/** High-contrast SVG. Error correction M survives a bit of screen glare. */
export function renderInviteQr(text: string): string {
  return renderSVG(text, {
    ecc: 'M',
    border: 2,
    pixelSize: 8,
    whiteColor: '#ffffff',
    blackColor: '#111111',
  });
}

export function buildRoomInvite(
  code: string,
  options: RoomInviteOptions = {},
): RoomInvitePayload {
  const roomCode = code.trim().toUpperCase();
  const checkWord = options.checkWord?.trim() || '';
  const url = buildJoinUrl(roomCode, options);
  return {
    title: `TeslaWithMe: ${roomCode}`,
    text:
      `Join my TeslaWithMe room: ${roomCode}\n` +
      (checkWord ? `Check word: ${checkWord}\n` : '') +
      'Open the link, allow location access, and you are ready to join the map.',
    url,
  };
}

export async function shareRoom(code: string, options: RoomInviteOptions = {}): Promise<ShareResult> {
  const invite = buildRoomInvite(code, options);

  if (typeof navigator.share === 'function') {
    try {
      await navigator.share(invite);
      return 'shared';
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        return 'cancelled';
      }
    }
  }

  if (navigator.clipboard?.writeText) {
    await navigator.clipboard.writeText(`${invite.text}\n${invite.url}`);
    return 'copied';
  }

  return 'unsupported';
}

export function readInviteFromUrl(url: string = window.location.href): PrefilledInvite {
  const parsed = new URL(url);
  return {
    roomCode: parsed.searchParams.get('room')?.trim().toUpperCase() ?? '',
    checkWord: parsed.searchParams.get('check')?.trim() ?? '',
  };
}

function buildInviteUrl(code: string, checkWord: string, baseUrl: string = getAppUrl()): string {
  const url = new URL(baseUrl);
  url.searchParams.set('room', code);
  if (checkWord) url.searchParams.set('check', checkWord);
  else url.searchParams.delete('check');
  return url.toString();
}

function getAppUrl(): string {
  return new URL('/', window.location.href).toString();
}
